import React, { useState, useEffect, useRef } from "react";
import { Card, Button } from "reactstrap";
import { Icon } from "../../components/Component";
import { useLocation, useSearchParams, useParams } from "react-router-dom";
import LedgerSummary from "./LedgerSummary";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../components/Component";
import { useDispatch, useSelector } from "react-redux";
import { updateMenuData } from "../../store/slices/misc/menuData";
import { updateReportInDetailModalSlice } from "../../store/slices/expenses/ReportInDetailView";
import TopMenu from "../../route/topMenu";
import { expensesApis } from "../../api/Expenses";
import dayjs from "dayjs";
import ReportInDetailModal from "./ReportInDetailModal";

interface ExpenseReport {
  id: string;
  reportName: string;
  status: string;
  submittedDate: string;
  approvedDate?: string;
  totalAmount: number;
  expenses: number;
  total: string;
  amountToBeReimbursed: string;
  submitter?: string;
  dateRange?: string;
}

interface LedgerEntry {
  id: number;
  transaction: string;
  dateRange: string;
  amountIn: number;
  amountInText: string;
  amountOut: number;
  userTotalIn: number;
  userTotalOut: number;
  userApprovedTotalIn: number;
  userApprovedTotalOut: number;
  status: "DRAFT" | "APPROVED" | "PENDING" | "REJECTED";
  expenseCount?: number;
  paginationId?: any;
  expenseReportId?: number;
  budgetLineId?: number;
  orgId?: number;
  projectId?: number;
  businessPurpose?: string;
  startDate?: string;
  endDate?: string;
  reportStatus?: string;
  createdBy?: number;
  createdByName?: string;
  updatedBy?: number;
  createdAt?: string;
  updatedAt?: string;
  budgetValue?: string;
  totalIn?: number;
  totalOut?: number;
  balance?: number;
  approvalRequired?: boolean;
  expenses?: any[];
  expenseApprovalHierarchy?: any[];
}

const Ledger: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams<{ projectId: string }>();
  const projectIdFromUrl = params.projectId;
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [projectIdState, setProjectIdState] = useState<string | number | null>(null);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [endOfList, setEndOfList] = useState(false);
  const [paginationId, setPaginationId] = useState<any>(null);
  const lastEntryRef = useRef<HTMLDivElement | null>(null);

  const [headerTotals, setHeaderTotals] = useState({
    approvedTotalIn: 0,
    approvedTotalOut: 0,
    approvedBalance: 0
  });
  const [headerLoading, setHeaderLoading] = useState(false);

  const { approvedTotalIn, approvedTotalOut, approvedBalance } = headerTotals;

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location, dispatch]);

  // Project ID Management: Extract projectId from URL params first, then location state
  useEffect(() => {
    const { id, projectId, projectIdFromState } = location.state || {};
    const urlProjectId = searchParams.get('projectId');
    // Use URL param first, then location state, then search params
    const currentProjectId = projectIdFromUrl || id || projectId || projectIdFromState || urlProjectId;

    // Validate that projectId is a valid number
    const numericProjectId = currentProjectId ? Number(currentProjectId) : null;
    if (numericProjectId && !isNaN(numericProjectId) && numericProjectId !== projectIdState) {
      setProjectIdState(numericProjectId);
    }
  }, [location.state, searchParams, projectIdState, projectIdFromUrl]);

  useEffect(() => {
    const checkMobileView = () => {
      setMobileView(window.innerWidth < 768);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);

    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  useEffect(() => {
    sm
      ? document.body.classList.add("toggle-shown")
      : document.body.classList.remove("toggle-shown");
  }, [sm]);

  // Lazy Loading: Intersection Observer for infinite scroll
  useEffect(() => {
    if (!lastEntryRef.current || endOfList) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading) {
          setCurrentPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );

    observer.observe(lastEntryRef.current);
    return () => observer.disconnect();
  }, [ledgerEntries, loading, endOfList]);

  // Pagination: Load more data when page changes
  useEffect(() => {
    if (currentPage > 0 && projectIdState) {
      loadMoreData();
    }
  }, [currentPage]);

  // Data Loading: Load data when project changes
  useEffect(() => {
    if (projectIdState) {
      refreshLedgerData();
      fetchHeaderTotals();
    }
  }, [projectIdState]);

  // Header Totals: Fetch and display total in, out, and balance calculations
  const fetchHeaderTotals = async () => {
    // Validate projectIdState is a valid number
    if (!projectIdState || isNaN(Number(projectIdState))) return;

    setHeaderLoading(true);
    try {
      const isAdmin = currentUser?.genericRole?.toLowerCase() === "admin";

      const apiParams = isAdmin ? {} : { userId: currentUser?.id || currentUser?.userId };

      const response = isAdmin
        ? await expensesApis.fetchExpenseLedgerSummary(Number(projectIdState))
        : await expensesApis.fetchExpenseLedgerSummary(Number(projectIdState), apiParams);

      if (response && response.status && response.data) {
        const { approvedTotalIn = 0, approvedTotalOut = 0, approvedBalance = 0 } = response.data;
        setHeaderTotals({
          approvedTotalIn,
          approvedTotalOut,
          approvedBalance
        });
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
    } finally {
      setHeaderLoading(false);
    }
  };

  // Real-time Updates: Refresh ledger data and totals
  const refreshLedgerData = () => {
    setEndOfList(false);
    setLedgerEntries([]);
    setCurrentPage(0);
    setPaginationId(null);
  };

  // Lazy Loading: Load more ledger entries for infinite scroll
  const loadMoreData = async () => {
    if (paginationId === undefined || (paginationId !== null && endOfList)) {
      return;
    }

    // Validate projectIdState is a valid number
    if (!projectIdState || isNaN(Number(projectIdState))) {
      return;
    }

    setLoading(true);
    try {
      if (projectIdState) {
        const isAdmin = currentUser?.genericRole?.toLowerCase() === "admin";

        const apiParams = {
          pageSize: 15, // Load 15 entries at a time
          pageDiff: 0,
          sortOrder: 'DESC',
          direction: paginationId ? 'F' : 'P',
          paginationId: paginationId,
          projectId: Number(projectIdState),
          reportStatus: 'APPROVED', // Filter only approved reports
          ...(isAdmin ? {} : { userId: currentUser?.id || currentUser?.userId }),
        };

        const response = await expensesApis.fetchReports(Number(projectIdState), apiParams);


        const ledgerData: LedgerEntry[] = [];

        if (response && response.status) {
          const reportsList = response.data?.list || [];
          
          // Additional client-side filtering to ensure only approved reports
          const approvedReports = reportsList.filter((report: any) => 
            report.reportStatus && report.reportStatus.toLowerCase() === 'approved'
          );

          if (approvedReports.length < apiParams.pageSize || !approvedReports) {
            setEndOfList(true);
          }

          if (approvedReports.length > 0) {
            setPaginationId(approvedReports[approvedReports.length - 1]?.paginationId || null);
          }

          approvedReports.forEach((report: any) => {
            const totalIn = report.totalIn || 0;
            const totalOut = report.totalOut || 0;
            const userApprovedTotalIn = report.userApprovedTotalIn || 0;
            const userApprovedTotalOut = report.userApprovedTotalOut || 0;
            const expenseCount = report.expenses?.length || 0;

            ledgerData.push({
              id: report.expenseReportId || Date.now(),
              transaction: report.reportName || 'Unnamed Report',
              dateRange: report.startDate && report.endDate
                ? `${dayjs(report.startDate).format('DD-MMM-YYYY')} - ${dayjs(report.endDate).format('DD-MMM-YYYY')}`
                : report.createdAt
                  ? dayjs(report.createdAt).format('DD-MMM-YYYY')
                  : dayjs().format('DD-MMM-YYYY'),
              amountIn: totalIn,
              amountInText: totalIn > 0 ? `₹${totalIn.toLocaleString()}.00` : '₹0.00',
              amountOut: totalOut,
              userTotalIn: userApprovedTotalIn,
              userTotalOut: userApprovedTotalOut,
              userApprovedTotalIn: userApprovedTotalIn,
              userApprovedTotalOut: userApprovedTotalOut,
              status: report.reportStatus || 'APPROVED',
              expenseCount: expenseCount,
              paginationId: report.paginationId,
              expenseReportId: report.expenseReportId,
              budgetLineId: report.budgetLineId,
              orgId: report.orgId,
              projectId: report.projectId,
              businessPurpose: report.businessPurpose,
              startDate: report.startDate,
              endDate: report.endDate,
              reportStatus: report.reportStatus,
              createdBy: report.createdBy,
              updatedBy: report.updatedBy,
              createdAt: report.createdAt,
              updatedAt: report.updatedAt,
              budgetValue: report.budgetValue,
              totalIn: report.totalIn,
              totalOut: report.totalOut,
              balance: report.balance,
              approvalRequired: report.approvalRequired,
              expenses: report.expenses,
              expenseApprovalHierarchy: report.expenseApprovalHierarchy
            });
          });
        }

        if (ledgerData.length > 0) {
          setLedgerEntries((prev: any) => [...prev, ...ledgerData]);
        }
      } else {
        setLedgerEntries([]);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
    } finally {
      setLoading(false);
    }
  };

  // Initial Data Loading: Separate useEffect for initial load
  useEffect(() => {
    if (projectIdState && currentPage === 0 && paginationId === null) {
      loadMoreData();
    }
  }, [projectIdState, currentPage, paginationId]);

  // Report Integration: Open detailed report breakdown in modal
  const handleOpenReport = (entry: LedgerEntry) => {
    const expenseReportId = entry.expenseReportId || entry.id;
    const totalAmount = entry.amountOut || 0;
    const expenseCount = entry.expenseCount || 1;
    const amountToBeReimbursed = totalAmount - (entry.amountIn || 0);

    const report: ExpenseReport = {
      id: expenseReportId.toString(),
      reportName: entry.transaction,
      status: entry.reportStatus || entry.status,
      submittedDate: entry.createdAt ? dayjs(entry.createdAt).format('DD-MMM-YYYY') : dayjs().format('DD-MMM-YYYY'),
      approvedDate: entry.updatedAt ? dayjs(entry.updatedAt).format('DD-MMM-YYYY') : undefined,
      totalAmount: totalAmount,
      expenses: expenseCount,
      total: `₹${totalAmount.toLocaleString()}.00`,
      amountToBeReimbursed: `₹${amountToBeReimbursed.toLocaleString()}.00`,
      submitter: entry.createdBy?.toString(),
      dateRange: entry.dateRange
    };

    const modalData = {
      id: expenseReportId.toString(),
      open: true,
      reportName: entry.transaction,
      activeTab: "expenses",
      projectResponse: {
        id: Number(entry.projectId || projectIdState),
        name: location.state?.projectName || "Project",
        projectId: Number(entry.projectId || projectIdState)
      },
      extra: {
        status: entry.reportStatus || entry.status,
        submittedDate: entry.createdAt ? dayjs(entry.createdAt).format('DD-MMM-YYYY') : dayjs().format('DD-MMM-YYYY'),
        approvedDate: entry.updatedAt ? dayjs(entry.updatedAt).format('DD-MMM-YYYY') : undefined,
        totalAmount: totalAmount,
        expenses: expenseCount,
        total: `₹${totalAmount.toLocaleString()}.00`,
        amountToBeReimbursed: `₹${amountToBeReimbursed.toLocaleString()}.00`,
        submitter: entry.createdBy?.toString(),
        dateRange: entry.dateRange,
        paginationId: entry.paginationId,
        createdBy: entry.createdBy,
        createdByName: entry.createdByName || entry.createdBy?.toString(),
        expenseReportId: entry.expenseReportId,
        budgetLineId: entry.budgetLineId,
        orgId: entry.orgId,
        businessPurpose: entry.businessPurpose,
        startDate: entry.startDate,
        endDate: entry.endDate,
        budgetValue: entry.budgetValue,
        totalIn: entry.totalIn,
        totalOut: entry.totalOut,
        balance: entry.balance,
        approvalRequired: entry.approvalRequired,
        expensesList: entry.expenses,
        expenseApprovalHierarchy: entry.expenseApprovalHierarchy
      },
      allowEdit: true
    };
    dispatch(updateReportInDetailModalSlice(modalData));
  };


  // Report Integration: Handle view report action
  const handleViewReport = (entry: LedgerEntry) => {
    handleOpenReport(entry);
  };

  return (
    <>
      <Head title="Approved Reports Ledger" />
      <Content>
        <Card className="card-bordered">
            <div className="card-inner card-inner-lg" style={{ width: '100%' }}>
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">Approved Reports Ledger</BlockTitle>
                    <p style={{ margin: 0, color: '#666', fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif' }}>
                      Approved expense reports and transaction history
                    </p>
                  </BlockHeadContent>
                  <BlockHeadContent>
                    <div className="toggle-wrap nk-block-tools-toggle d-flex align-items-center gap-2">
                      <Button
                        className={`btn-icon btn-trigger toggle-expand me-n1`}
                        onClick={() => setSm(!sm)}
                      >
                        <Icon name="menu-alt-r"></Icon>
                      </Button>
                    </div>
                  </BlockHeadContent>
                  <div style={{
                    marginTop: '16px',
                    textAlign: 'right'
                  }}>
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      justifyContent: 'flex-end',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{
                        display: 'inline-block',
                        padding: '10px 14px',
                      }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '500',
                          color: '#6b7280',
                          fontFamily: 'Inter, Roboto, sans-serif',
                          marginBottom: '2px'
                        }}>
                          TOTAL IN
                        </div>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#0369a1',
                          fontFamily: 'Inter, Roboto, sans-serif'
                        }}>
                           {headerLoading ? (
                             <Icon name="spinner" className="spinner-border spinner-border-sm" />
                           ) : (
                             `₹ ${approvedTotalIn.toLocaleString()}.00`
                           )}
                        </span>
                      </div>

                      <div style={{
                        display: 'inline-block',
                        padding: '10px 14px',
                      }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '500',
                          color: '#6b7280',
                          fontFamily: 'Inter, Roboto, sans-serif',
                          marginBottom: '2px'
                        }}>
                          TOTAL OUT
                        </div>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#dc2626',
                          fontFamily: 'Inter, Roboto, sans-serif'
                        }}>
                           {headerLoading ? (
                             <Icon name="spinner" className="spinner-border spinner-border-sm" />
                           ) : (
                             `₹ ${approvedTotalOut.toLocaleString()}.00`
                           )}
                        </span>
                      </div>

                      <div style={{
                        display: 'inline-block',
                        padding: '10px 14px',
                      }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '500',
                          color: '#6b7280',
                          fontFamily: 'Inter, Roboto, sans-serif',
                          marginBottom: '2px'
                        }}>
                          TOTAL BALANCE
                        </div>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '600',
                           color: approvedBalance < 0 ? '#dc2626' : '#059669',
                          fontFamily: 'Inter, Roboto, sans-serif'
                        }}>
                           {headerLoading ? (
                             <Icon name="spinner" className="spinner-border spinner-border-sm" />
                           ) : (
                             <>₹ {approvedBalance.toLocaleString()}.00</>
                           )}
                        </span>
                      </div>
                    </div>
                  </div>

                </BlockBetween>
              </BlockHead>
              <Block>
                 <LedgerSummary
                   entries={ledgerEntries}
                   balance={approvedBalance}
                   onView={handleViewReport}
                   loading={loading}
                   lastEntryRef={lastEntryRef}
                   endOfList={endOfList}
                 />
              </Block>
            </div>
        </Card>
      </Content>
      <ReportInDetailModal />
    </>
  );
};

export default Ledger;